import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'

type TransportMode = {
  id: string
  type_mode: 'PICKUP' | 'DELIVERY'
  label: string
  description: string | null
  pricing_mode: 'FIXED' | 'PER_KM'
  fixed_price: number | null
  price_per_km: number | null
  min_price: number | null
  max_distance_km: number | null
  is_atelier: boolean
  is_active: boolean
}

const AdminTransportPage: React.FC = () => {
  const [modes, setModes] = useState<TransportMode[]>([])
  const [loading, setLoading] = useState(true)

  // modes utilisés dans des commandes (pickup_mode_id / delivery_mode_id)
  const [usedModeIds, setUsedModeIds] = useState<Set<string>>(new Set())

  // Formulaire création
  const [typeMode, setTypeMode] = useState<'PICKUP' | 'DELIVERY'>('PICKUP')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [pricingMode, setPricingMode] = useState<'FIXED' | 'PER_KM'>('FIXED')
  const [fixedPrice, setFixedPrice] = useState<number | ''>('')
  const [pricePerKm, setPricePerKm] = useState<number | ''>('')
  const [minPrice, setMinPrice] = useState<number | ''>('')
  const [maxDistance, setMaxDistance] = useState<number | ''>(50)
  const [isAtelier, setIsAtelier] = useState(false)

  // Édition
  const [editingModeId, setEditingModeId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editPricingMode, setEditPricingMode] = useState<'FIXED' | 'PER_KM'>('FIXED')
  const [editFixedPrice, setEditFixedPrice] = useState<number | ''>('')
  const [editPricePerKm, setEditPricePerKm] = useState<number | ''>('')
  const [editMinPrice, setEditMinPrice] = useState<number | ''>('')
  const [editMaxDistance, setEditMaxDistance] = useState<number | ''>('')
  const [editIsAtelier, setEditIsAtelier] = useState(false)

  const load = async () => {
    setLoading(true)

    const { data, error } = await supabase
      .from('transport_modes')
      .select('*')
      .order('type_mode', { ascending: true })

    if (error) console.error(error)
    setModes(data ?? [])

    // Charger les ids de modes utilisés
    const { data: used, error: eUsed } = await supabase
      .from('orders')
      .select('pickup_mode_id, delivery_mode_id')

    if (eUsed) {
      console.error(eUsed)
      setUsedModeIds(new Set())
    } else {
      const ids = new Set<string>()
      for (const row of used ?? []) {
        if (row.pickup_mode_id) ids.add(row.pickup_mode_id as string)
        if (row.delivery_mode_id) ids.add(row.delivery_mode_id as string)
      }
      setUsedModeIds(ids)
    }

    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    const { error } = await supabase.from('transport_modes').insert({
      type_mode: typeMode,
      label,
      description: description || null,
      pricing_mode: pricingMode,
      fixed_price: pricingMode === 'FIXED' ? Number(fixedPrice || 0) : null,
      price_per_km: pricingMode === 'PER_KM' ? Number(pricePerKm || 0) : null,
      min_price: pricingMode === 'PER_KM' ? Number(minPrice || 0) : null,
      max_distance_km: maxDistance === '' ? null : Number(maxDistance),
      is_atelier: isAtelier
    })

    if (error) {
      console.error(error)
      alert("Erreur lors de la création du mode de transport.")
      return
    }

    setLabel('')
    setDescription('')
    setFixedPrice('')
    setPricePerKm('')
    setMinPrice('')
    setMaxDistance(50)
    setIsAtelier(false)
    void load()
  }

  const handleToggleActive = async (mode: TransportMode) => {
    const { error } = await supabase
      .from('transport_modes')
      .update({ is_active: !mode.is_active })
      .eq('id', mode.id)
    if (error) {
      console.error(error)
      alert("Erreur lors de la mise à jour du mode.")
      return
    }
    void load()
  }

  const handleDeleteMode = async (mode: TransportMode) => {
    const used = usedModeIds.has(mode.id)
    if (used) {
      alert("Ce mode est déjà utilisé dans au moins une commande, il ne peut pas être supprimé.")
      return
    }

    const ok = window.confirm(
      `Supprimer le mode "${mode.label}" ?\n\nAttention: cette suppression est définitive.`
    )
    if (!ok) return

    const { error } = await supabase
      .from('transport_modes')
      .delete()
      .eq('id', mode.id)

    if (error) {
      console.error(error)
      alert("Impossible de supprimer ce mode.")
      return
    }

    void load()
  }

  const startEditMode = (mode: TransportMode) => {
    setEditingModeId(mode.id)
    setEditLabel(mode.label)
    setEditDescription(mode.description ?? '')
    setEditPricingMode(mode.pricing_mode)
    setEditFixedPrice(mode.fixed_price ?? '')
    setEditPricePerKm(mode.price_per_km ?? '')
    setEditMinPrice(mode.min_price ?? '')
    setEditMaxDistance(mode.max_distance_km ?? '')
    setEditIsAtelier(mode.is_atelier)
  }

  const cancelEditMode = () => {
    setEditingModeId(null)
  }

  const saveEditMode = async (mode: TransportMode) => {
    const used = usedModeIds.has(mode.id)
    if (used) {
      alert("Ce mode est déjà utilisé dans au moins une commande, il ne peut plus être modifié.")
      return
    }

    const { error } = await supabase
      .from('transport_modes')
      .update({
        label: editLabel,
        description: editDescription || null,
        pricing_mode: editPricingMode,
        fixed_price: editPricingMode === 'FIXED' ? Number(editFixedPrice || 0) : null,
        price_per_km: editPricingMode === 'PER_KM' ? Number(editPricePerKm || 0) : null,
        min_price: editPricingMode === 'PER_KM' ? Number(editMinPrice || 0) : null,
        max_distance_km: editMaxDistance === '' ? null : Number(editMaxDistance),
        is_atelier: editIsAtelier
      })
      .eq('id', mode.id)

    if (error) {
      console.error(error)
      alert("Erreur lors de la mise à jour du mode.")
      return
    }

    setEditingModeId(null)
    void load()
  }

  const renderModeLine = (mode: TransportMode) => {
    const used = usedModeIds.has(mode.id)
    const isEditing = editingModeId === mode.id

    const pricingText =
      mode.pricing_mode === 'FIXED'
        ? `${(mode.fixed_price ?? 0).toFixed(2)} CHF`
        : `${mode.price_per_km} CHF/km, min ${(mode.min_price ?? 0).toFixed(2)} CHF`

    return (
      <div
        key={mode.id}
        style={{
          borderTop: '1px solid rgba(148,163,184,.4)',
          padding: '.75rem 0',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem'
        }}
      >
        <div style={{ flex: 1 }}>
          {isEditing ? (
            <>
              <div className="grid-2" style={{ marginBottom: '.5rem' }}>
                <div>
                  <label className="label">Libellé</label>
                  <input
                    className="input"
                    value={editLabel}
                    data-testid={`transport-edit-label-${mode.id}`}
                    onChange={e => setEditLabel(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Lieu atelier ?</label>
                  <input
                    type="checkbox"
                    data-testid={`transport-edit-isatelier-${mode.id}`}
                    checked={editIsAtelier}
                    onChange={e => setEditIsAtelier(e.target.checked)}
                  />{' '}
                  <span style={{ fontSize: '.8rem' }}>Atelier</span>
                </div>
              </div>
              <div style={{ marginBottom: '.5rem' }}>
                <label className="label">Description</label>
                <input
                  className="input"
                  value={editDescription}
                  data-testid={`transport-edit-description-${mode.id}`}
                  onChange={e => setEditDescription(e.target.value)}
                />
              </div>
              <div className="grid-2" style={{ marginBottom: '.5rem' }}>
                <div>
                  <label className="label">Mode de tarification</label>
                  <select
                    className="select"
                    value={editPricingMode}
                    data-testid={`transport-edit-pricing-${mode.id}`}
                    onChange={e => setEditPricingMode(e.target.value as 'FIXED' | 'PER_KM')}
                  >
                    <option value="FIXED">Prix fixe</option>
                    <option value="PER_KM">Prix au km</option>
                  </select>
                </div>
                <div>
                  <label className="label">Distance max (km)</label>
                  <input
                    className="input"
                    type="number"
                    value={editMaxDistance}
                    data-testid={`transport-edit-maxdistance-${mode.id}`}
                    onChange={e =>
                      setEditMaxDistance(e.target.value === '' ? '' : Number(e.target.value))
                    }
                  />
                </div>
              </div>
              {editPricingMode === 'FIXED' ? (
                <div style={{ marginBottom: '.5rem' }}>
                  <label className="label">Prix fixe (CHF)</label>
                  <input
                    className="input"
                    type="number"
                    step="0.1"
                    value={editFixedPrice}
                    data-testid={`transport-edit-fixedprice-${mode.id}`}
                    onChange={e =>
                      setEditFixedPrice(e.target.value === '' ? '' : Number(e.target.value))
                    }
                  />
                </div>
              ) : (
                <div className="grid-2" style={{ marginBottom: '.5rem' }}>
                  <div>
                    <label className="label">Prix au km (CHF)</label>
                    <input
                      className="input"
                      type="number"
                      step="0.1"
                      value={editPricePerKm}
                      data-testid={`transport-edit-priceperkm-${mode.id}`}
                      onChange={e =>
                        setEditPricePerKm(e.target.value === '' ? '' : Number(e.target.value))
                      }
                    />
                  </div>
                  <div>
                    <label className="label">Prix minimum (CHF)</label>
                    <input
                      className="input"
                      type="number"
                      step="0.1"
                      value={editMinPrice}
                        data-testid={`transport-edit-minprice-${mode.id}`}
                        onChange={e =>
                          setEditMinPrice(e.target.value === '' ? '' : Number(e.target.value))
                        }
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600 }}>
                [{mode.type_mode === 'PICKUP' ? 'Prise en charge' : 'Livraison'}] {mode.label}
              </div>
              <div style={{ fontSize: '.8rem', color: '#9ca3af' }}>{mode.description}</div>
              <div style={{ fontSize: '.8rem', marginTop: '.25rem' }}>
                <strong>Tarification :</strong> {pricingText}
                {mode.max_distance_km && ` · max ${mode.max_distance_km} km`}
              </div>
              {mode.is_atelier && (
                <div style={{ fontSize: '.75rem', color: '#22c55e', marginTop: '.25rem' }}>
                  Ce lieu est l’atelier par défaut
                </div>
              )}
              {used && (
                <div style={{ fontSize: '.75rem', color: '#fbbf24', marginTop: '.25rem' }}>
                  Ce mode est déjà utilisé dans au moins une commande, il n’est plus modifiable.
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
          <div className="chip">
            <span className="chip-dot" />
            {mode.is_active ? 'Actif' : 'Inactif'}
          </div>
          <button
            type="button"
            className="btn"
            data-testid={`transport-toggle-${mode.id}`}
            onClick={() => handleToggleActive(mode)}
          >
            {mode.is_active ? 'Désactiver' : 'Activer'}
          </button>

          {isEditing ? (
            <>
              <button
                type="button"
                data-testid={`transport-save-${mode.id}`}
                className="btn btn-primary"
                onClick={() => saveEditMode(mode)}
              >
                Enregistrer
              </button>
              <button
                type="button"
                data-testid={`transport-cancel-${mode.id}`}
                className="btn btn-ghost"
                onClick={cancelEditMode}
              >
                Annuler
              </button>
            </>
          ) : (
            <>
              {!used && (
                <button
                  type="button"
                  data-testid={`transport-edit-${mode.id}`}
                  className="btn btn-ghost"
                  onClick={() => startEditMode(mode)}
                >
                  Modifier
                </button>
              )}
              <button
                type="button"
                data-testid={`transport-delete-${mode.id}`}
                className="btn btn-ghost"
                onClick={() => handleDeleteMode(mode)}
              >
                Supprimer
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="card-title">Prises en charge & livraisons</h2>

      {loading && <p>Chargement…</p>}

      {!loading && (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>Modes existants</h3>
            {modes.length === 0 && (
              <div style={{ fontSize: '.8rem', color: '#9ca3af' }}>Aucun mode défini.</div>
            )}
            {modes.map(renderModeLine)}
          </div>

          {/* Création d’un mode */}
          <form onSubmit={handleCreate}>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>Nouveau mode</h3>

            <div className="grid-2" style={{ marginBottom: '.75rem' }}>
              <div>
                <label className="label">Type</label>
                <select
                  className="select"
                  value={typeMode}
                  data-testid="transport-create-type"
                  onChange={e => setTypeMode(e.target.value as 'PICKUP' | 'DELIVERY')}
                >
                  <option value="PICKUP">Prise en charge</option>
                  <option value="DELIVERY">Livraison</option>
                </select>
              </div>
              <div>
                <label className="label">Lieu atelier ?</label>
                <input
                  type="checkbox"
                  data-testid="transport-create-isatelier"
                  checked={isAtelier}
                  onChange={e => setIsAtelier(e.target.checked)}
                />{' '}
                <span style={{ fontSize: '.8rem' }}>Ce mode correspond à l’atelier</span>
              </div>
            </div>

            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Libellé</label>
              <input data-testid="transport-create-label" className="input" value={label} onChange={e => setLabel(e.target.value)} />
            </div>
            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Description</label>
                <input
                data-testid="transport-create-description"
                className="input"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>

            <div className="grid-2" style={{ marginBottom: '.75rem' }}>
              <div>
                <label className="label">Mode de tarification</label>
                <select
                  className="select"
                  value={pricingMode}
                  data-testid="transport-create-pricing"
                  onChange={e => setPricingMode(e.target.value as 'FIXED' | 'PER_KM')}
                >
                  <option value="FIXED">Prix fixe</option>
                  <option value="PER_KM">Prix au km</option>
                </select>
              </div>
              <div>
                <label className="label">Distance max (km)</label>
                  <input
                    data-testid="transport-create-maxdistance"
                    className="input"
                    type="number"
                    value={maxDistance}
                    onChange={e =>
                      setMaxDistance(e.target.value === '' ? '' : Number(e.target.value))
                    }
                  />
              </div>
            </div>

            {pricingMode === 'FIXED' ? (
              <div style={{ marginBottom: '.75rem' }}>
                <label className="label">Prix fixe (CHF)</label>
                <input
                  className="input"
                  type="number"
                  step="0.1"
                  value={fixedPrice}
                  data-testid="transport-create-fixedprice"
                  onChange={e =>
                    setFixedPrice(e.target.value === '' ? '' : Number(e.target.value))
                  }
                />
              </div>
            ) : (
              <div className="grid-2" style={{ marginBottom: '.75rem' }}>
                <div>
                  <label className="label">Prix au km (CHF)</label>
                  <input
                    className="input"
                    type="number"
                    step="0.1"
                    value={pricePerKm}
                      data-testid="transport-create-priceperkm"
                      onChange={e =>
                        setPricePerKm(e.target.value === '' ? '' : Number(e.target.value))
                      }
                  />
                </div>
                <div>
                  <label className="label">Prix minimum (CHF)</label>
                  <input
                    className="input"
                    type="number"
                    step="0.1"
                    value={minPrice}
                    data-testid="transport-create-minprice"
                    onChange={e =>
                      setMinPrice(e.target.value === '' ? '' : Number(e.target.value))
                    }
                  />
                </div>
              </div>
            )}

            <button data-testid="transport-create-submit" className="btn btn-primary" type="submit">
              Créer le mode
            </button>
          </form>
        </>
      )}
    </div>
  )
}

export default AdminTransportPage
